import moment from 'moment';
import { DetenteurEntity } from '../../../../entities/detenteur.entity';
import { OperationEntity } from '../../../../entities/operation.entity';
import { TransactionEntity } from '../../../../entities/transaction.entity';
import {
  ExportFormat,
  ExportMetadata,
  exportPremiumFile,
} from '../../../../shared/exportFiles';
import { AssetManagerType } from '../../../../redux/userStore/reducer';

export const generateDetenteurDataToExport = (detenteurs: any[]) => {
  let toExport: any[] = [];
  let index = 1;
  for (let det of detenteurs) {
    toExport.push({
      Index: index,
      'Code client': det.code_referent,
      Nom: det.nom,
      Prénom: det.prenom,
      Email: det.email,
      Adresse: det.adresse,
      Nationalité: det.nationalite,
      Téléphone: det.mobile,
      Gestionnaire: `${det.createur.nom} ${det.createur.prenom}`,
      'Qte total titre': det.qteTotal,
      'Montant total': det.montantTotal,
      'P&L': det.pnl,
      'Code agence': det.agence.code,
      Agence: det.agence.nom,
      'Code partenaire': det.partenaire.code_referent,
      Partenaire: det.partenaire.nom,
      'Date création': `${moment(det.created_at).format(
        'DD/MM/YYYY HH:mm:ss',
      )}`,
      'Date modification': `${moment(det.updated_at).format(
        'DD/MM/YYYY HH:mm:ss',
      )}`,
    });
    index += 1;
  }

  return toExport;
};

export const generateTitreDetailDataToExport = (detenteurs: any[]) => {
  let toExport: any[] = [];
  let index = 1;
  for (let det of detenteurs) {
    for (let detB of det.detailTitres) {
      toExport.push({
        Index: index,
        Code: detB.parentCode,
        'Sous code': detB.subCode,
        Emetteur: detB.titre.pays,
        Instrument: detB.titre.instrument,
        'Date Emission': detB.titre.dateEmission,
        'Date Maturite': detB.titre.dateMaturite,
        'Duree Maturite': detB.titre.dureeMaturite,
        Etat: detB.etat,
        Quantite: detB.qte,
        'Montant total': detB.montant,
        Prix: detB.prixNominale,
        'Décote (%)': detB.decote,
        'Coupon (%)': detB.titre.tauxMarginal,
        'Rendement (%)': detB.tauxRendement,
        'P&L': detB.pnl,
        'Code origin': detB.originCode,
        'Code client': detB.detenteur.code_referent,
        'Nom client': detB.detenteur.nom + ' ' + detB.detenteur.prenom,
        'Date acquisition': `${moment(detB.dateAcquisition).format(
          'DD/MM/YYYY',
        )}`,
        'Coupon Couru à Payer': detB.couponCouruPayer,
        'Coupon à Percevoir': detB.couponPercevoir,
        'Commission Total': detB.commissionTotal,
      });
      index += 1;
    }
  }

  return toExport;
};

export const exportPortefeuilleData = (
  detenteurs: DetenteurEntity[],
  format: ExportFormat,
  metadata?: ExportMetadata,
) => {
  const detenteurData = generateDetenteurDataToExport(detenteurs);
  const TitreDetailsData = generateTitreDetailDataToExport(detenteurs);

  exportPremiumFile({
    fileName: 'Portefeuille client',
    metadata,
    title: 'Portefeuille client',
    sheets: [
      { data: detenteurData, name: 'Etat des detenteurs' },
      { data: TitreDetailsData, name: 'Detail du portefeuille' },
    ],
  }, format);
};

export const generateMoranjeClientRows = (clients: AssetManagerType[]) =>
  clients.map((client, index) => ({
    Index: index + 1,
    Client: client.nom,
    Email: client.email,
    Adresse: client.adresse,
    Contact: client.contact,
    'Statut abonnement':
      Date.parse(client.fin_abonnement || '') >= Date.now()
        ? 'En cours'
        : 'Expiré',
    'Fin abonnement': client.fin_abonnement
      ? moment(client.fin_abonnement).format('DD/MM/YYYY')
      : '',
    'Nombre abonnements': client.abonnement?.length || 0,
    Achats: client.achat,
    Ventes: client.vente,
    'Date création': client.created_at
      ? moment(client.created_at).format('DD/MM/YYYY HH:mm:ss')
      : '',
    'Date modification': client.updated_at
      ? moment(client.updated_at).format('DD/MM/YYYY HH:mm:ss')
      : '',
  }));

export const exportMoranjeClientsData = (
  clients: AssetManagerType[],
  format: ExportFormat,
  metadata?: ExportMetadata,
) => {
  exportPremiumFile({
    fileName: 'Clients MoranjETrade',
    metadata,
    title: 'Clients MoranjETrade',
    sheets: [
      {
        data: generateMoranjeClientRows(clients),
        name: 'Registre clients',
      },
    ],
  }, format);
};

const getCommission = (row: OperationEntity | TransactionEntity) =>
  Math.ceil(
    Number(row.commIntermediation || 0) +
      Number(row.commConservation || 0) +
      Number(row.commDroitGarde || 0) +
      Number(row.commBrokerage || 0),
  );

const generateOperationRows = (operations: OperationEntity[]) =>
  operations.map((operation, index) => {
    const montant = Number(operation.qteRestant || 0) * Number(operation.prixNominale || 0);
    const commission = getCommission(operation);

    return {
      Index: index + 1,
      'Asset manager': `${(operation as any).assetManager?.code_referent || ''} - ${(operation as any).assetManager?.nom || ''}`,
      Partenaire: `${(operation as any).partenaire?.code_referent || ''} - ${(operation as any).partenaire?.nom || ''}`,
      Agence: `${(operation as any).agence?.code || ''} - ${(operation as any).agence?.nom || ''}`,
      Emetteur: operation.titre?.pays,
      Instrument: operation.titre?.instrument,
      'Code titre': operation.titre?.code,
      Détenteur: `${operation.detenteur?.code_referent || ''} - ${operation.detenteur?.nom || ''} ${operation.detenteur?.prenom || ''}`,
      Gestionnaire: `${(operation as any).utilisateur?.nom || ''} ${(operation as any).utilisateur?.prenom || ''}`,
      Opération: operation.typeOperation,
      Statut: operation.statut,
      'Date opération': operation.dateOperation,
      'Quantité initiale': operation.qteOriginal,
      'Quantité restante': operation.qteRestant,
      Coupon: operation.tauxInteret,
      Décote: operation.decote,
      Rendement: operation.tauxRendement,
      Prix: operation.prixNominale,
      Montant: montant,
      Commission: commission,
      'Net estimé': Math.ceil(montant + Number(operation.couponCouruPayer || 0) + commission),
      'Date création': moment(operation.created_at).format('DD/MM/YYYY HH:mm:ss'),
    };
  });

const generateTransactionRows = (transactions: TransactionEntity[]) =>
  transactions.map((transaction, index) => {
    const montant = Number(transaction.qte || 0) * Number(transaction.prixNominale || 0);
    const commission = getCommission(transaction);

    return {
      Index: index + 1,
      'Code transaction': transaction.code,
      'Asset manager': `${(transaction as any).assetManager?.code_referent || ''} - ${(transaction as any).assetManager?.nom || ''}`,
      Partenaire: `${(transaction as any).partenaire?.code_referent || ''} - ${(transaction as any).partenaire?.nom || ''}`,
      Agence: `${(transaction as any).agence?.code || ''} - ${(transaction as any).agence?.nom || ''}`,
      Emetteur: transaction.titre?.pays,
      Instrument: transaction.titre?.instrument,
      'Code titre': transaction.titre?.code,
      Détenteur: `${transaction.detenteur?.code_referent || ''} - ${transaction.detenteur?.nom || ''} ${transaction.detenteur?.prenom || ''}`,
      Gestionnaire: `${(transaction as any).utilisateur?.nom || ''} ${(transaction as any).utilisateur?.prenom || ''}`,
      Opération: transaction.typeOperation,
      Statut: transaction.statut,
      'Date opération': transaction.dateOperation || transaction.created_at,
      Quantité: transaction.qte,
      Coupon: transaction.tauxInteret,
      Décote: transaction.decote,
      Rendement: transaction.tauxRendement,
      Prix: transaction.prixNominale,
      Montant: montant,
      Commission: commission,
      'Net estimé': Math.ceil(montant + Number(transaction.couponCouruPayer || 0) + commission),
      'Date création': moment(transaction.created_at).format('DD/MM/YYYY HH:mm:ss'),
      'Date modification': moment(transaction.updated_at).format('DD/MM/YYYY HH:mm:ss'),
    };
  });

export const exportOperationReportingData = ({
  buyOrders,
  finalized,
  matched,
  sellOrders,
}: {
  buyOrders: OperationEntity[];
  finalized: TransactionEntity[];
  matched: TransactionEntity[];
  sellOrders: OperationEntity[];
},
  format: ExportFormat,
  metadata?: ExportMetadata,
) => {
  exportPremiumFile({
    fileName: 'Reporting operations',
    metadata,
    title: 'Reporting operations',
    sheets: [
      { data: generateOperationRows(buyOrders), name: "Ordres d'achat" },
      { data: generateOperationRows(sellOrders), name: 'Ordres de vente' },
      { data: generateTransactionRows(matched), name: 'Transactions matchées' },
      { data: generateTransactionRows(finalized), name: 'Transactions finalisées' },
    ],
  }, format);
};
